import React from 'react';
import { Box, Typography } from '@mui/material';
import DosenTooltip from '../../../../../../../../components/ui/DosenTooltip';
import { ACCESS_COLOR, ACCESS_LABEL, AudienceAccess, ViewAudience } from './requirements';

export const FONT = 'Inter';
export const TEXT = 'var(--Text-text-primary, #FDFDFD)';
export const TEXT_2 = 'var(--Text-text-secondary, #B6B6B6)';
export const TEXT_3 = 'var(--Text-text-tertiary, #868686)';
export const LINE = 'var(--Stroke-Border-border-dim, #2A2A2A)';
export const BRAND = 'var(--Stroke-highlight, #F79420)';
export const SURFACE = '#1C1C1C';

const BY_ROLE_COLOR = '#B6B6B6';

// The dashboard capability-score tooltips' text (TIP_TEXT / TIP_HEADING in
// learning-lifecycle/TeamCapabilityScore.tsx), copied: importing that file
// pulls the capability API into everything that shows a requirement.
const TIP_TEXT = { fontFamily: 'Inter, sans-serif', fontSize: 11.5, lineHeight: 1.5, color: '#B6B6B6' };
const TIP_HEADING = { ...TIP_TEXT, color: '#FDFDFD', fontWeight: 700 };

// ── Tooltips, laid out like the dashboard's capability score ones ───────────

/** A bulleted list in a tooltip. */
export const TipBullets: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="ul" sx={{ m: 0, mt: '4px', pl: '16px', '& li': { ...TIP_TEXT, py: '1px' } }}>
    {children}
  </Box>
);

/** A column's hover: the audience group's name, then who is in it, list by list. */
export const AudienceTip: React.FC<{ audience: ViewAudience }> = ({ audience }) => (
  <Box>
    <Typography sx={TIP_HEADING}>{audience.name}</Typography>
    {audience.parts.length > 0 ? (
      <TipBullets>
        {audience.parts.map((p) => (
          <li key={p.label}>
            <Box component="span" sx={{ color: '#FDFDFD', fontWeight: 600 }}>
              {p.label}:
            </Box>{' '}
            {p.values.join(', ')}
          </li>
        ))}
      </TipBullets>
    ) : (
      <Typography sx={TIP_TEXT}>Anyone in the course’s audience.</Typography>
    )}
  </Box>
);

const accessLabel = (v: AudienceAccess['value']) => (v === 'byRole' ? 'By Role' : ACCESS_LABEL[v]);
const accessColor = (v: AudienceAccess['value']) => (v === 'byRole' ? BY_ROLE_COLOR : ACCESS_COLOR[v]);

/** A cell's hover: the audience group, what it must do here, and role by role when that differs. */
const AccessTip: React.FC<{ audienceName: string; access: AudienceAccess }> = ({ audienceName, access }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    <Typography sx={TIP_HEADING}>{audienceName}</Typography>
    <Typography sx={TIP_TEXT}>{access.detail}</Typography>
    {access.roles && (
      <TipBullets>
        {/* Two teams can each have a role of the same name. */}
        {access.roles.map((r, i) => (
          <li key={`${i}-${r.name}`}>
            {r.name}:{' '}
            <Box component="span" sx={{ color: ACCESS_COLOR[r.access], fontWeight: 600 }}>
              {ACCESS_LABEL[r.access]}
            </Box>
          </li>
        ))}
      </TipBullets>
    )}
  </Box>
);

/** An audience group's access to a lesson, as coloured text (By Role when its roles differ). */
export const AccessText: React.FC<{ audienceName: string; access: AudienceAccess }> = ({ audienceName, access }) => (
  <DosenTooltip title={<AccessTip audienceName={audienceName} access={access} />}>
    <Typography
      component="span"
      sx={{
        fontFamily: FONT,
        fontSize: 12.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        cursor: 'default',
        color: accessColor(access.value)
      }}
    >
      {accessLabel(access.value)}
    </Typography>
  </DosenTooltip>
);

/**
 * A course with no audience groups of its own yet (every course from before
 * them): what is shown is the one group it would get from its current
 * audience (PRD R9), so nothing changes for its learners.
 */
export const DerivedNote: React.FC<{ sx?: object }> = ({ sx }) => (
  <Typography data-testid="requirements-derived" sx={{ fontFamily: FONT, fontSize: 12.5, color: TEXT_3, ...sx }}>
    This course has no audience groups yet. Shown is the one it would get from its current audience: Read &amp; Assess,
    as today, so nothing changes for its learners.
  </Typography>
);

/** The grid's ⓘ: how requirements work. */
export const HowTip: React.FC = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    <Typography sx={TIP_HEADING}>How it works</Typography>
    <Typography sx={TIP_TEXT}>
      Every lesson’s requirements come from the course’s audience groups, set at Details.
    </Typography>
    <TipBullets>
      <li>
        <Box component="span" sx={{ color: ACCESS_COLOR.readAssess, fontWeight: 600 }}>
          Read &amp; Assess
        </Box>
        : read and are assessed on the lessons for their own roles’ capabilities, and read the rest.
      </li>
      <li>
        <Box component="span" sx={{ color: ACCESS_COLOR.readOnly, fontWeight: 600 }}>
          Read Only
        </Box>
        : read every lesson, to stay in the loop.
      </li>
      <li>
        <Box component="span" sx={{ color: BY_ROLE_COLOR, fontWeight: 600 }}>
          By Role
        </Box>
        : the group’s roles differ on that lesson. Hover for each.
      </li>
      <li>Nobody is in both groups.</li>
    </TipBullets>
  </Box>
);
